import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Edit2, Trash2, RefreshCw, Package, CheckCircle, MapPinned, Handshake, Image as ImageIcon, X, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';
import { getCategoryBadgeColor } from '../../../utils/statusColors';
import MediaPicker, { mediaServeUrl } from '../../../components/admin/MediaPicker';
import PublishToGalleryModal from '../../../components/admin/PublishToGalleryModal';
import { processAndUpload, ACCEPTED_ACCEPT_ATTR } from '../../../lib/mediaUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Small live-totals panel shown at the top of the Updates admin tab.
// READ-ONLY per spec — corrections must be made by editing the
// underlying distribution record; there is no way to type into a
// total here. Data source: /api/public/impact-stats/distributions
// which recomputes on every request from community_updates.
const DistributionTotalsPanel = ({ refreshKey }) => {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/impact-stats/distributions`);
      setTotals(r.data || {});
    } catch { setTotals({}); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!totals) return null;

  const Card = ({ icon: Icon, label, value, tid }) => (
    <div className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-2.5 flex items-center gap-2.5" data-testid={tid}>
      <div className="w-8 h-8 bg-[#FEE2E2] rounded-md flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#BA1826]" />
      </div>
      <div className="min-w-0">
        <div className="font-heading text-base font-bold text-[#0A0A0A] leading-tight">
          {(value || 0).toLocaleString()}
        </div>
        <p className="text-[10px] text-[#6B7280] leading-tight">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-4" data-testid="distribution-totals-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-bold text-sm text-[#0A0A0A]">Live Distribution Totals</h3>
          <p className="text-[11px] text-[#6B7280]">
            Read-only. Recomputed from records — edit an individual distribution below to change these numbers.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="text-xs text-[#4B5563] hover:text-[#BA1826] disabled:opacity-50 flex items-center gap-1"
          data-testid="dist-totals-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Card icon={Package}      label="Essential Items Distributed" value={totals.items_distributed}      tid="totals-items" />
        <Card icon={CheckCircle}  label="Distributions Completed"     value={totals.distributions_completed} tid="totals-dists" />
        <Card icon={MapPinned}    label="Community Locations"         value={totals.locations_reached}       tid="totals-locations" />
        <Card icon={Handshake}    label="Community Partners"          value={totals.partners_reached}        tid="totals-partners" />
      </div>
      {Array.isArray(totals.by_item_type) && totals.by_item_type.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-3" data-testid="totals-by-item-type">
          <p className="text-[10px] font-bold text-[#4B5563] uppercase tracking-wide mb-2">By Item Type</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
            {totals.by_item_type.map((row) => (
              <div key={row.item_type} className="flex items-baseline justify-between text-[11px]"
                   data-testid={`totals-type-${row.item_type.replace(/\s+/g, '-')}`}>
                <span className="text-[#4B5563] truncate">{row.item_type}</span>
                <span className="font-semibold text-[#0A0A0A] tabular-nums ml-2">{row.quantity.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const TestimonialsTab = ({ ctx }) => {
  const {
    testimonials, testimonialForm, setTestimonialForm,
    editingTestimonial, setEditingTestimonial,
    pendingTestimonials, approvedTestimonials,
    saveTestimonial, deleteTestimonial, fetchData
  } = ctx;

  return (
    <div className="space-y-6">
      {/* Add Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="testimonial-form">
        <h3 className="font-heading font-bold mb-4">{editingTestimonial ? 'Edit Story' : 'Add Story (Pre-Approved)'}</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <input type="text" placeholder="Name" value={testimonialForm.name}
            onChange={(e) => setTestimonialForm({...testimonialForm, name: e.target.value})}
            className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" data-testid="testimonial-name-input" />
          <input type="text" placeholder="Role (e.g., Volunteer, Donor)" value={testimonialForm.role}
            onChange={(e) => setTestimonialForm({...testimonialForm, role: e.target.value})}
            className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" />
          <input type="text" placeholder="Country (optional)" value={testimonialForm.country}
            onChange={(e) => setTestimonialForm({...testimonialForm, country: e.target.value})}
            className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={testimonialForm.is_featured}
              onChange={(e) => setTestimonialForm({...testimonialForm, is_featured: e.target.checked})} />
            Featured on Homepage
          </label>
        </div>
        <textarea placeholder="Quote / Story" value={testimonialForm.quote} rows={3}
          onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
          className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm mb-3" data-testid="testimonial-quote-input" />
        <div className="flex gap-2">
          <button onClick={saveTestimonial} className="btn-primary text-sm" data-testid="save-testimonial-btn">
            <Save className="w-4 h-4" /> {editingTestimonial ? 'Update' : 'Add Story'}
          </button>
          {editingTestimonial && (
            <button onClick={() => { setEditingTestimonial(null); setTestimonialForm({ name: '', role: '', quote: '', country: '', is_featured: true }); }} className="btn-secondary text-sm">Cancel</button>
          )}
        </div>
      </div>

      {/* Pending Stories */}
      {pendingTestimonials.length > 0 && (
        <div>
          <h4 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
            Pending Review ({pendingTestimonials.length})
          </h4>
          {pendingTestimonials.map((t) => (
            <div key={t.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 mb-3" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm">{t.name} <span className="text-[#4B5563] font-normal">— {t.role || 'Donor'}</span></p>
                  {t.country && <span className="text-xs text-[#BA1826]">{t.country}</span>}
                </div>
                <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Pending</span>
              </div>
              <p className="text-sm text-[#4B5563] italic mb-3">"{t.quote}"</p>
              <div className="flex gap-2">
                <button onClick={async () => { await axios.put(`${BACKEND_URL}/api/admin/testimonials/${t.id}`, { status: 'approved', is_featured: true }, { withCredentials: true }); fetchData(); }} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700" data-testid={`approve-${t.id}`}>
                  Approve & Feature
                </button>
                <button onClick={async () => { await axios.put(`${BACKEND_URL}/api/admin/testimonials/${t.id}`, { status: 'approved' }, { withCredentials: true }); fetchData(); }} className="text-xs bg-[#0A0A0A] text-white px-3 py-1.5 rounded hover:bg-gray-800">
                  Approve
                </button>
                <button onClick={() => deleteTestimonial(t.id)} className="text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded hover:bg-red-200">
                  Reject & Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approved Stories */}
      {approvedTestimonials.length > 0 && (
        <div>
          <h4 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Approved ({approvedTestimonials.length})
          </h4>
          {approvedTestimonials.map((t) => (
            <div key={t.id} className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-3" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold text-sm">{t.name} <span className="text-[#4B5563] font-normal">— {t.role}</span></p>
                  {t.country && <span className="text-xs text-[#BA1826]">{t.country}</span>}
                </div>
                <div className="flex items-center gap-2">
                  {t.is_featured && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">Featured</span>}
                  <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded">Approved</span>
                  <button onClick={() => { setEditingTestimonial(t); setTestimonialForm({ name: t.name, role: t.role, quote: t.quote, country: t.country, is_featured: t.is_featured }); }} className="text-[#BA1826]"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => deleteTestimonial(t.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-[#4B5563] italic">"{t.quote}"</p>
            </div>
          ))}
        </div>
      )}

      {testimonials.length === 0 && (
        <p className="text-center text-[#4B5563] text-sm py-8">No stories yet. Visitor submissions will appear here for your review.</p>
      )}
    </div>
  );
};

export const UpdatesTab = ({ ctx }) => {
  const {
    updates, updateForm, setUpdateForm,
    editingUpdate, setEditingUpdate,
    saveUpdate, deleteUpdate
  } = ctx;

  const isDistribution = updateForm.type === 'distribution';
  // Bump this key after every save/delete to force the totals panel to refetch.
  const [totalsKey, setTotalsKey] = useState(0);
  // Media Library picker state
  const [featuredPickerOpen, setFeaturedPickerOpen] = useState(false);
  const [additionalPickerOpen, setAdditionalPickerOpen] = useState(false);
  // Publish-to-Main-Gallery modal state
  const [publishOpen, setPublishOpen] = useState(false);

  // Direct-from-device upload state. Kept per-section so a failing
  // Additional upload doesn't block a Featured retry (and vice-versa).
  const featuredFileRef = useRef(null);
  const additionalFileRef = useRef(null);
  const [featuredUpload, setFeaturedUpload] = useState({ busy: false, pct: 0, error: '' });
  const [additionalUpload, setAdditionalUpload] = useState({ busy: false, pct: 0, error: '' });

  const handleFeaturedUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset the input value up-front so picking the same file twice fires change.
    if (featuredFileRef.current) featuredFileRef.current.value = '';
    if (!file) return;
    setFeaturedUpload({ busy: true, pct: 0, error: '' });
    try {
      const out = await processAndUpload([file], (pct) =>
        setFeaturedUpload((s) => ({ ...s, pct }))
      );
      if (out.length === 0) throw new Error('Upload failed.');
      setUpdateForm({ ...updateForm, image_url: out[0].url });
      setFeaturedUpload({ busy: false, pct: 100, error: '' });
    } catch (err) {
      setFeaturedUpload({ busy: false, pct: 0, error: err.message || 'Upload failed.' });
    }
  };

  // Universal Media Standard: max 12 photos TOTAL per update
  // (featured counts as one of the 12). Compute the additional-photo
  // budget from whether a featured photo is set. Backend enforces the
  // same rule with HTTP 422 — this UI just keeps admins from
  // discovering it after a save.
  const MAX_TOTAL_PHOTOS = 12;
  const additionalMax = MAX_TOTAL_PHOTOS - ((updateForm.image_url || '').trim() ? 1 : 0);
  const additionalCount = (updateForm.additional_photos || []).length;
  const additionalRemaining = Math.max(0, additionalMax - additionalCount);

  const handleAdditionalUpload = async (e) => {
    const filesList = Array.from(e.target.files || []);
    if (additionalFileRef.current) additionalFileRef.current.value = '';
    if (filesList.length === 0) return;
    if (additionalRemaining === 0) {
      const withFeatured = (updateForm.image_url || '').trim();
      setAdditionalUpload({
        busy: false, pct: 0,
        error: withFeatured
          ? `You already have ${additionalMax} additional photos (featured counts as 1 of ${MAX_TOTAL_PHOTOS}).`
          : `You already have ${MAX_TOTAL_PHOTOS} photos.`,
      });
      return;
    }
    const clipped = filesList.slice(0, additionalRemaining);
    const trimmedNote = filesList.length > additionalRemaining
      ? ` Only the first ${additionalRemaining} were uploaded (${MAX_TOTAL_PHOTOS}-photo total limit).`
      : '';
    setAdditionalUpload({ busy: true, pct: 0, error: '' });
    try {
      const out = await processAndUpload(clipped, (pct) =>
        setAdditionalUpload((s) => ({ ...s, pct }))
      );
      const urls = out.map((o) => o.url);
      setUpdateForm({
        ...updateForm,
        additional_photos: [...(updateForm.additional_photos || []), ...urls].slice(0, additionalMax),
      });
      const partial = out.partialErrors ? ` (${out.partialErrors[0]})` : '';
      setAdditionalUpload({
        busy: false, pct: 100,
        error: (trimmedNote || partial) ? `${trimmedNote}${partial}`.trim() : '',
      });
    } catch (err) {
      setAdditionalUpload({ busy: false, pct: 0, error: err.message || 'Upload failed.' });
    }
  };

  const handleSave = async () => {
    await saveUpdate();
    setTotalsKey((k) => k + 1);
  };
  const handleDelete = async (id) => {
    await deleteUpdate(id);
    setTotalsKey((k) => k + 1);
  };

  const removeAdditionalPhoto = (url) => {
    setUpdateForm({
      ...updateForm,
      additional_photos: (updateForm.additional_photos || []).filter((u) => u !== url),
    });
  };

  return (
    <div className="space-y-6">
      {/* Live totals panel — shown ONLY at the top of the Updates tab,
          not injected into other content sections. Read-only. */}
      <DistributionTotalsPanel refreshKey={totalsKey} />

      {/* Add/Edit Update Form */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="update-form">
        <h3 className="font-heading font-bold mb-4">
          {editingUpdate ? 'Edit Update' : 'Create New Update'}
        </h3>

        {/* Update Type selector — spec: reuse Admin → Updates for both
            community updates and physical distribution records. */}
        <div className="mb-4">
          <label className="text-xs font-bold text-[#4B5563] uppercase tracking-wide block mb-2">Update Type</label>
          <div className="inline-flex bg-[#F1F5F9] rounded-md p-1" data-testid="update-type-toggle">
            <button type="button"
              onClick={() => setUpdateForm({ ...updateForm, type: 'community' })}
              className={`px-4 py-1.5 text-xs font-semibold rounded transition-colors ${!isDistribution ? 'bg-white text-[#BA1826] shadow' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
              data-testid="type-community">Community Update</button>
            <button type="button"
              onClick={() => setUpdateForm({ ...updateForm, type: 'distribution' })}
              className={`px-4 py-1.5 text-xs font-semibold rounded transition-colors ${isDistribution ? 'bg-white text-[#BA1826] shadow' : 'text-[#4B5563] hover:text-[#0A0A0A]'}`}
              data-testid="type-distribution">Distribution</button>
          </div>
          {isDistribution && (
            <p className="text-[11px] text-[#6B7280] mt-2">
              Only <strong>Completed</strong> distributions contribute to public impact totals.
              Money fields are not editable here — donations remain system-controlled.
            </p>
          )}
        </div>

        <div className="space-y-3 mb-4">
          <input type="text" placeholder={isDistribution ? 'Distribution title (e.g. Sacred Heart Community Distribution)' : 'Update title'}
            value={updateForm.title}
            onChange={(e) => setUpdateForm({...updateForm, title: e.target.value})}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
            data-testid="update-title-input" />
          <textarea placeholder={isDistribution ? 'Short description (what was distributed, to whom, notable outcomes)' : 'Description / body text'}
            value={updateForm.description}
            onChange={(e) => setUpdateForm({...updateForm, description: e.target.value})} rows={4}
            className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none resize-none"
            data-testid="update-description-input" />

          {/* Distribution-only fields */}
          {isDistribution && (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Status</label>
                  <select value={updateForm.status}
                    onChange={(e) => setUpdateForm({...updateForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-status-select">
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Item Type</label>
                  <select value={updateForm.item_type}
                    onChange={(e) => setUpdateForm({...updateForm, item_type: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-item-type-select">
                    <option value="Hygiene Kits">Hygiene Kits</option>
                    <option value="Food Packages">Food Packages</option>
                    <option value="School Supplies">School Supplies</option>
                    <option value="Backpacks">Backpacks</option>
                    <option value="Clothing">Clothing</option>
                    <option value="Emergency Supplies">Emergency Supplies</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Quantity Distributed</label>
                  <input type="number" min="0" value={updateForm.quantity}
                    onChange={(e) => setUpdateForm({...updateForm, quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-quantity-input" />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Location</label>
                  <input type="text" placeholder="e.g. Cambria Heights, NY"
                    value={updateForm.location}
                    onChange={(e) => setUpdateForm({...updateForm, location: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-location-input" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Partner / Organization</label>
                  <input type="text" placeholder="e.g. Sacred Heart Community"
                    value={updateForm.partner}
                    onChange={(e) => setUpdateForm({...updateForm, partner: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-partner-input" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">People / Families Served (optional)</label>
                  <input type="number" min="0" value={updateForm.people_served}
                    onChange={(e) => setUpdateForm({...updateForm, people_served: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                    data-testid="dist-people-served-input" />
                </div>
              </div>
            </>
          )}

          {/* Community-only field */}
          {!isDistribution && (
            <div className="grid md:grid-cols-3 gap-3">
              <select value={updateForm.category}
                onChange={(e) => setUpdateForm({...updateForm, category: e.target.value})}
                className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="update-category-select">
                <option value="Fundraisers">Fundraisers</option>
                <option value="Community Support">Community Support</option>
                <option value="Health Initiatives">Health Initiatives</option>
              </select>
              <input type="date" value={updateForm.date}
                onChange={(e) => setUpdateForm({...updateForm, date: e.target.value})}
                className="px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="update-date-input" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={updateForm.is_published}
                  onChange={(e) => setUpdateForm({...updateForm, is_published: e.target.checked})}
                  data-testid="update-published-checkbox" />
                Published
              </label>
            </div>
          )}

          {/* Shared: date + published toggle for distributions (rendered
              separately from the category row above) */}
          {isDistribution && (
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Date</label>
                <input type="date" value={updateForm.date}
                  onChange={(e) => setUpdateForm({...updateForm, date: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-sm text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="update-date-input" />
              </div>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={updateForm.is_published}
                  onChange={(e) => setUpdateForm({...updateForm, is_published: e.target.checked})}
                  data-testid="update-published-checkbox" />
                <span>Show Publicly<br /><span className="text-[10px] text-[#6B7280] font-normal">visible on /impact + /distributions</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox"
                  checked={updateForm.include_in_totals !== false}
                  onChange={(e) => setUpdateForm({...updateForm, include_in_totals: e.target.checked})}
                  data-testid="update-include-in-totals-checkbox" />
                <span>Include in Impact Totals<br /><span className="text-[10px] text-[#6B7280] font-normal">counts toward the Impact page numbers</span></span>
              </label>
            </div>
          )}

          {/* Featured photo — same UI for Distribution AND Community
              Update. Upload from Device / Pick from Media Library.
              Both branches share the durable Emergent Object Storage
              pipeline. Testids kept as `dist-*` for backward compat
              with the Distribution regression suite. */}
          {(
            <div>
              <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">Featured Photo</label>
              {updateForm.image_url ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="w-20 h-20 rounded overflow-hidden bg-[#F1F5F9] border border-[#E5E7EB] flex-shrink-0">
                    <img src={updateForm.image_url} alt="Featured" className="w-full h-full object-cover" data-testid="dist-featured-preview" />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button type="button"
                      onClick={() => featuredFileRef.current && featuredFileRef.current.click()}
                      disabled={featuredUpload.busy}
                      className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="dist-featured-upload-replace-btn">
                      {featuredUpload.busy
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {featuredUpload.pct}%</>
                        : <><Upload className="w-3.5 h-3.5" /> Replace from Device</>}
                    </button>
                    <button type="button"
                      onClick={() => setFeaturedPickerOpen(true)}
                      className="btn-secondary text-xs px-3 py-1.5"
                      data-testid="dist-featured-replace-btn">Replace from Library</button>
                    <button type="button"
                      onClick={() => setUpdateForm({...updateForm, image_url: ''})}
                      className="text-xs px-3 py-1.5 border border-[#E5E7EB] rounded text-red-500 hover:bg-red-50"
                      data-testid="dist-featured-remove-btn">Remove</button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button type="button"
                    onClick={() => featuredFileRef.current && featuredFileRef.current.click()}
                    disabled={featuredUpload.busy}
                    className="px-4 py-2 border border-dashed border-[#BA1826] text-[#BA1826] rounded text-xs font-semibold flex items-center gap-2 hover:bg-[#FEF2F2] disabled:opacity-50"
                    data-testid="dist-featured-upload-btn">
                    {featuredUpload.busy
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {featuredUpload.pct}%</>
                      : <><Upload className="w-3.5 h-3.5" /> Upload from Device</>}
                  </button>
                  <button type="button"
                    onClick={() => setFeaturedPickerOpen(true)}
                    className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded text-xs font-semibold flex items-center gap-2 hover:bg-[#F8F9FA]"
                    data-testid="dist-featured-pick-btn">
                    <ImageIcon className="w-3.5 h-3.5" /> Pick from Media Library
                  </button>
                </div>
              )}
              <input ref={featuredFileRef} type="file"
                accept={ACCEPTED_ACCEPT_ATTR}
                onChange={handleFeaturedUpload}
                className="hidden"
                data-testid="dist-featured-file-input" />
              {featuredUpload.error && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="dist-featured-upload-error">{featuredUpload.error}</p>
              )}
              <p className="text-[10px] text-[#6B7280] mt-1">
                Upload directly from your device (JPG, PNG, WebP up to 20MB) or reuse an image already in the Media Library.
              </p>
            </div>
          )}

          {/* Additional photos — Distribution AND Community Update.
              Multi-select Media Library picker + direct device upload
              + per-photo remove. Both flows respect the 12-photo cap. */}
          {(
            <div>
              <label className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide block mb-1">
                Additional Photos ({additionalCount}/{additionalMax})
                {(updateForm.image_url || '').trim() && (
                  <span className="ml-1 text-[9px] font-normal normal-case text-[#9CA3AF]">
                    · featured counts as 1 of {MAX_TOTAL_PHOTOS}
                  </span>
                )}
                {editingUpdate && ((updateForm.additional_photos || []).length > 0 || (updateForm.image_url || '').trim()) && (
                  <button type="button"
                    onClick={() => setPublishOpen(true)}
                    className="ml-2 text-[10px] font-semibold text-[#BA1826] hover:text-[#9E1420] normal-case tracking-normal inline-flex items-center gap-0.5"
                    data-testid="dist-publish-to-gallery-btn">
                    <ImageIcon className="w-3 h-3" /> Publish to Main Gallery
                  </button>
                )}
              </label>
              {(updateForm.additional_photos || []).length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2" data-testid="dist-additional-list">
                  {(updateForm.additional_photos || []).map((u) => (
                    <div key={u} className="relative aspect-square rounded overflow-hidden bg-[#F1F5F9] border border-[#E5E7EB] group"
                         data-testid="dist-additional-item">
                      <img src={u} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeAdditionalPhoto(u)}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-90 hover:opacity-100"
                        aria-label="Remove photo"
                        data-testid="dist-additional-remove-btn">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button"
                  onClick={() => additionalFileRef.current && additionalFileRef.current.click()}
                  disabled={additionalUpload.busy || additionalRemaining === 0}
                  className="px-4 py-2 border border-dashed border-[#BA1826] text-[#BA1826] rounded text-xs font-semibold flex items-center gap-2 hover:bg-[#FEF2F2] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="dist-additional-upload-btn">
                  {additionalUpload.busy
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {additionalUpload.pct}%</>
                    : <><Upload className="w-3.5 h-3.5" /> Upload Photos from Device</>}
                </button>
                <button type="button"
                  onClick={() => setAdditionalPickerOpen(true)}
                  disabled={additionalRemaining === 0}
                  className="px-4 py-2 border border-[#E5E7EB] text-[#4B5563] rounded text-xs font-semibold flex items-center gap-2 hover:bg-[#F8F9FA] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="dist-additional-pick-btn">
                  <ImageIcon className="w-3.5 h-3.5" /> Add from Media Library
                </button>
              </div>
              <input ref={additionalFileRef} type="file" multiple
                accept={ACCEPTED_ACCEPT_ATTR}
                onChange={handleAdditionalUpload}
                className="hidden"
                data-testid="dist-additional-file-input" />
              {additionalUpload.error && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="dist-additional-upload-error">{additionalUpload.error}</p>
              )}
              <p className="text-[10px] text-[#6B7280] mt-1">
                Upload directly from your device (JPG, PNG, WebP up to 20MB each) or reuse images from the Media Library. Max {MAX_TOTAL_PHOTOS} photos total per update (featured counts as one).
              </p>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave}
            disabled={!updateForm.title.trim() || (!isDistribution && !updateForm.description.trim())}
            className="btn-primary text-sm disabled:opacity-50" data-testid="save-update-btn">
            <Save className="w-4 h-4" /> {editingUpdate ? 'Update' : (isDistribution ? 'Create Distribution' : 'Create Update')}
          </button>
          {editingUpdate && (
            <button onClick={() => { setEditingUpdate(null); setUpdateForm({
              type: 'community', title: '', description: '',
              category: 'Community Support', image_url: '', date: '', is_published: true,
              status: 'completed', item_type: 'Hygiene Kits', quantity: 0,
              people_served: 0, location: '', partner: '', additional_photos: [],
              include_in_totals: true,
            }); }}
              className="btn-secondary text-sm">Cancel</button>
          )}
        </div>

        {/* Media Library pickers */}
        <MediaPicker
          open={featuredPickerOpen}
          onClose={() => setFeaturedPickerOpen(false)}
          mode="single"
          onPick={(url) => setUpdateForm({ ...updateForm, image_url: url })}
        />
        <MediaPicker
          open={additionalPickerOpen}
          onClose={() => setAdditionalPickerOpen(false)}
          mode="multi"
          excludeUrls={updateForm.additional_photos || []}
          onPick={(url) => setUpdateForm({
            ...updateForm,
            additional_photos: [...(updateForm.additional_photos || []), url].slice(0, additionalMax),
          })}
        />
        {editingUpdate && (
          <PublishToGalleryModal
            open={publishOpen}
            onClose={() => setPublishOpen(false)}
            sourceType={isDistribution ? 'distribution' : 'community_update'}
            sourceId={editingUpdate.id}
            defaultAlbumSlug={isDistribution ? 'food-distribution' : 'community-outreach'}
            photos={[
              ...((updateForm.image_url || '').trim()
                ? [{
                    id: updateForm.image_url,
                    image_url: updateForm.image_url,
                    thumb_url: updateForm.image_url,
                    title: updateForm.title || null,
                  }]
                : []),
              ...((updateForm.additional_photos || []).map((u) => ({
                id: u, image_url: u, thumb_url: u,
                title: updateForm.title || null,
              }))),
            ]}
          />
        )}
      </div>

      {/* Updates List */}
      {updates.length === 0 ? (
        <p className="text-center text-[#4B5563] text-sm py-8">No updates yet. Create your first community update or distribution above.</p>
      ) : (
        <div className="space-y-3">
          {updates.map((update) => {
            const isDist = update.type === 'distribution';
            return (
            <div key={update.id} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`admin-update-${update.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-heading font-bold text-sm truncate">{update.title}</h4>
                    {isDist && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${update.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {update.status === 'completed' ? 'Completed' : 'Upcoming'}
                      </span>
                    )}
                    {isDist && update.include_in_totals === false && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-700" title="Excluded from Impact totals">
                        Not counted
                      </span>
                    )}
                    {!update.is_published && (
                      <span className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded flex-shrink-0">Hidden</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#4B5563] flex-wrap">
                    {isDist ? (
                      <>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Distribution</span>
                        {update.quantity > 0 && <span>{update.quantity} {update.item_type}</span>}
                        {update.location && <span>· {update.location}</span>}
                        {update.date && <span>· {update.date}</span>}
                      </>
                    ) : (
                      <>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getCategoryBadgeColor(update.category)}`}>{update.category}</span>
                        <span>{update.date}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                  <button onClick={() => {
                    setEditingUpdate(update);
                    setUpdateForm({
                      type: update.type === 'distribution' ? 'distribution' : 'community',
                      title: update.title || '',
                      description: update.description || '',
                      category: update.category || 'Community Support',
                      image_url: update.image_url || '',
                      date: update.date || '',
                      is_published: update.is_published !== false,
                      status: update.status || 'completed',
                      item_type: update.item_type || 'Hygiene Kits',
                      quantity: update.quantity || 0,
                      people_served: update.people_served || 0,
                      location: update.location || '',
                      partner: update.partner || '',
                      additional_photos: update.additional_photos || [],
                      include_in_totals: update.include_in_totals !== false,
                    });
                  }} className="text-[#BA1826]" data-testid={`edit-update-${update.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(update.id)} className="text-red-400" data-testid={`delete-update-${update.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-[#4B5563] line-clamp-2">{update.description}</p>
            </div>
          );})}
        </div>
      )}
    </div>
  );
};
